import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import MyLoadingButton from "../../../components/buttons/MyLoadingButton";
import { Box, Stack, TextField } from "@mui/material";
import Wrapper from "../../../components/wrappers/Wrapper";
import { CREATE_PRICE } from "../../../enums/PermissionTitles";
import UserHint from "../../../components/display/UserHint";
import { useServiceMutation } from "../../admin/clinic/components/warehouse/components/services/hooks/useGetServices";
import CheckboxForm from "../../../components/form/CheckboxForm";
import { mandatory } from "../../../utils/Functions";
import ServiceDurationForm from "../../admin/clinic/components/warehouse/components/services/components/ServiceDurationForm";
import ServicePriceFields from "../../admin/clinic/components/warehouse/components/services/components/ServicePriceFields";
import { useGetWarehousePreferences } from "../../admin/clinic/components/warehouse/hooks/useGetWarehousePreferences";
import { parseDecimal, deriveTotalPrice } from "../../admin/clinic/components/warehouse/utils/decimalInput";
export const serviceMaxTitle = 50;
export const serviceMaxDesc = 70;
export default function NewPriceForm({ clinicId, onCreated }) {
  const { t, i18n } = useTranslation();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
  } = useFormContext();

  const mutation = useServiceMutation(clinicId);
  const { data: warehousePreferences } = useGetWarehousePreferences(clinicId);
  const priceEntryMode = warehousePreferences?.priceEntryMode ?? "NETTO";
  const defaultVat = warehousePreferences?.defaultVat;
  const currency = warehousePreferences?.currency ?? "EUR";

  const currentLanguage = i18n.language;

  const priceValue = watch("price");
  const pdvValue = watch("pdv");
  const derivedPrice = React.useMemo(
    () => deriveTotalPrice(priceValue, pdvValue, priceEntryMode),
    [priceValue, pdvValue, priceEntryMode],
  );

  // Same default-VAT behavior as WarehouseItemForm.jsx: once a price is
  // typed and PDV is still blank/untouched, seed it from the clinic's
  // configured default VAT rather than leaving it for the user to type
  // every time.
  React.useEffect(() => {
    if (defaultVat == null) {
      return;
    }
    const price = parseDecimal(priceValue);
    if (price == null || Number.isNaN(price)) {
      return;
    }
    if (getValues("pdv") === "") {
      setValue("pdv", String(defaultVat));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceValue, defaultVat]);

  const submitData = async () => {
    const data = watch();

    const enteredPrice = parseDecimal(data.price);
    // Once a price is entered, PDV always resolves to a real number (typed
    // value, the default-VAT autofill above, or 0 as a last resort) so the
    // backend always computes priceWithTax - mirrors WarehouseItemForm's
    // pdv ?? 0 fallback, but only once there's actually a price, so a
    // service left entirely price-less still submits price/pdv as null
    // rather than showing an unwanted "Price: 0" section.
    const pdv = enteredPrice != null ? (parseDecimal(data.pdv) ?? 0) : null;
    const price =
      priceEntryMode === "BRUTTO" && enteredPrice != null && pdv != null
        ? Number((enteredPrice / (1 + pdv / 100)).toFixed(2))
        : enteredPrice;

    var obj = {
      ...data,
      price,
      pdv,
      translations: {
        [currentLanguage]: data?.title,
      },
    };

    if (data?.description) {
      obj = {
        ...obj,
        descriptionTranslation: {
          [currentLanguage]: data?.description,
        },
      };
    }
    const success = await mutation.submitCallback(obj);
    if (success) {
      reset();
      onCreated?.();
    }
  };

  return (
    <form onSubmit={handleSubmit(submitData)}>
      <Stack mb={2}>
        <Wrapper direction="column" alignItems="flex-start" spacing={2}>
          <Controller
            name="title"
            defaultValue={""}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={mandatory(t("Title"))}
                fullWidth
                inputProps={{
                  maxLength: serviceMaxTitle,
                  "data-testid": "service.form.title-input",
                }}
                error={!!errors.title}
                helperText={errors.title ? errors.title.message : ""}
              />
            )}
          />

          <Controller
            name="description"
            defaultValue={""}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t("Description")}
                fullWidth
                inputProps={{
                  maxLength: serviceMaxDesc,
                  "data-testid": "service.form.description-input",
                }}
                error={!!errors.description}
                helperText={
                  errors.description ? errors.description.message : ""
                }
              />
            )}
          />
          <CheckboxForm
            hasBorder
            label={mandatory(t("MandatoryDescription"))}
            hint={"MandatoryDescriptionHint"}
            name="mandatoryDescription"
            defaulVal={false}
          />
          <ServiceDurationForm
            hint="ServiceDurationHint"
            testId="service.form.duration-select"
          />
          <ServicePriceFields
            control={control}
            errors={errors}
            priceEntryMode={priceEntryMode}
            derivedPrice={derivedPrice}
            currency={currency}
            testIdPrefix="service.form"
          />
          <Box
            sx={{
              ml: 2,
            }}
          >
            <MyLoadingButton
              message={t("Add")}
              isLoading={mutation.loading}
              permissions={[CREATE_PRICE]}
              testId="service.form.submit-button"
            />
          </Box>
          <UserHint hint={"AddServiceUserHint"} />
        </Wrapper>
      </Stack>
    </form>
  );
}
